import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Center,
  Spinner,
  Button,
  useDisclosure,
  Select,
  Flex
} from '@chakra-ui/react';
import PageTitle from '../components/PageTitle';
import RulesQuestionModal from '../components/RulesQuestionModal';
import { useAboutSettings } from '../hooks/useAboutSettings';
import { useMyLeagueStore } from '../store/myLeagueStore';
import '../styles/about_content.css';

type AboutTabKey = 'how-it-works' | 'entry-fees' | 'prize-pool' | 'key-league-dates' | 'rules';
type RulesTabKey = 'rules-of-the-game' | 'roster-management' | 'compliance' | 'free-agency' | 'trades' | 'drafts';

const rulesTabOptions = [
  { value: 'rules-of-the-game', label: 'Rules of the Game' },
  { value: 'roster-management', label: 'Roster Management' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'free-agency', label: 'Free Agency' },
  { value: 'trades', label: 'Trades' },
  { value: 'drafts', label: 'Drafts' },
];

/**
 * Extracts the inner HTML content from a full HTML document or returns the content as-is.
 * If the content is a full HTML document, it extracts the body or main content.
 * If it's already just content, it returns it unchanged.
 */
const extractHtmlContent = (htmlString: string | undefined): string => {
  if (!htmlString) return '';
  
  // Check if it's a full HTML document
  if (htmlString.trim().startsWith('<!DOCTYPE') || htmlString.trim().startsWith('<html')) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      // Try to get main content first, fallback to body
      const mainElement = doc.querySelector('main');
      if (mainElement) {
        return mainElement.innerHTML;
      }
      return doc.body.innerHTML;
    } catch (err) {
      console.error('Error parsing HTML:', err);
      return htmlString;
    }
  }
  
  // Already just content, return as-is
  return htmlString;
};

/**
 * Wraps all tables in the HTML content with a scrollable container div.
 * This ensures tables can scroll while maintaining proper border-collapse behavior.
 * Captions are placed outside the scroll container, above the table.
 */
const wrapTablesInScrollContainer = (htmlString: string): string => {
  if (!htmlString) return '';
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const tables = Array.from(doc.querySelectorAll('table'));
    
    tables.forEach((table) => {
      // Get the caption if it exists
      const caption = table.querySelector('caption');
      
      // Create wrapper div for scrolling
      const wrapper = doc.createElement('div');
      wrapper.className = 'table-scroll';
      
      // Clone the table
      const tableClone = table.cloneNode(true) as HTMLTableElement;
      
      // Remove caption from cloned table if it exists (we'll place it outside)
      if (caption) {
        const clonedCaption = tableClone.querySelector('caption');
        if (clonedCaption) {
          clonedCaption.remove();
        }
      }
      
      // Move cloned table into wrapper
      wrapper.appendChild(tableClone);
      
      // Create a container fragment to hold caption (if exists) and wrapper
      const fragment = doc.createDocumentFragment();
      
      // If caption exists, clone it and add it first (outside scroll container)
      if (caption) {
        const captionClone = caption.cloneNode(true) as HTMLTableCaptionElement;
        fragment.appendChild(captionClone);
      }
      
      // Add the wrapper with table
      fragment.appendChild(wrapper);
      
      // Replace the original table with the fragment contents
      const parent = table.parentNode;
      if (parent) {
        // Insert fragment before table
        parent.insertBefore(fragment, table);
        // Remove original table
        parent.removeChild(table);
      }
    });
    
    // Return the modified HTML
    return doc.body.innerHTML;
  } catch (err) {
    console.error('Error wrapping tables:', err);
    return htmlString;
  }
};

const HowItWorks: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const leagueId = myLeague?.id;

  // Call hook unconditionally (before any early returns)
  const { aboutSettings, isLoading, error } = useAboutSettings(leagueId);

  // Refs for content wrappers to handle interactive elements
  const howItWorksRef = useRef<HTMLDivElement | null>(null);
  const entryFeesRef = useRef<HTMLDivElement | null>(null);
  const prizePoolRef = useRef<HTMLDivElement | null>(null);
  const keyLeagueDatesRef = useRef<HTMLDivElement | null>(null);
  const draftsContainerRef = useRef<HTMLDivElement | null>(null);
  const draftsHandlerRef = useRef<((event: Event) => void) | null>(null);

  // State for inner Rules tab (always reset to "rules-of-the-game" when Rules outer tab is selected)
  const [activeRulesTab, setActiveRulesTab] = useState<RulesTabKey>('rules-of-the-game');

  // State for Rules Question modal
  const { isOpen: isRulesQuestionOpen, onOpen: onRulesQuestionOpen, onClose: onRulesQuestionClose } = useDisclosure();
  const [rulesQuestion, setRulesQuestion] = useState('');

  // Extract HTML content from database strings and wrap tables in scroll containers
  const howItWorksHtml = useMemo(
    () => wrapTablesInScrollContainer(extractHtmlContent(aboutSettings?.how_it_works?.content)),
    [aboutSettings?.how_it_works?.content]
  );

  const entryFeesHtml = useMemo(
    () => wrapTablesInScrollContainer(extractHtmlContent(aboutSettings?.entry_fees?.description)),
    [aboutSettings?.entry_fees?.description]
  );

  const prizePoolHtml = useMemo(
    () => wrapTablesInScrollContainer(extractHtmlContent(aboutSettings?.prize_pool?.description)),
    [aboutSettings?.prize_pool?.description]
  );

  const keyLeagueDatesHtml = useMemo(
    () => wrapTablesInScrollContainer(extractHtmlContent(aboutSettings?.key_league_dates_html)),
    [aboutSettings?.key_league_dates_html]
  );

  // Extract individual rules sections and wrap tables
  const rulesSections = useMemo(() => {
    const sections = aboutSettings?.rules?.sections;
    if (sections && typeof sections === 'object' && !Array.isArray(sections)) {
      return {
        rules_of_the_game: wrapTablesInScrollContainer(extractHtmlContent(sections.rules_of_the_game)),
        roster_management: wrapTablesInScrollContainer(extractHtmlContent(sections.roster_management)),
        compliance: wrapTablesInScrollContainer(extractHtmlContent(sections.compliance)),
        free_agency: wrapTablesInScrollContainer(extractHtmlContent(sections.free_agency)),
        trades: wrapTablesInScrollContainer(extractHtmlContent(sections.trades)),
        drafts: wrapTablesInScrollContainer(extractHtmlContent(sections.drafts)),
      };
    }
    return null;
  }, [aboutSettings?.rules?.sections]);

  // Get current About page from URL
  const currentAboutPage: AboutTabKey = (() => {
    const validPages: AboutTabKey[] = ['how-it-works', 'entry-fees', 'prize-pool', 'key-league-dates', 'rules'];
    return (tab && validPages.includes(tab as AboutTabKey)) ? (tab as AboutTabKey) : 'how-it-works';
  })();

  // Handle draft-type radio buttons in drafts section
  useEffect(() => {
    // Only run this logic when we are actually viewing the Drafts tab inside Rules
    if (currentAboutPage !== 'rules' || activeRulesTab !== 'drafts') {
      // Clean up any existing handler when we're not on Drafts
      const currentRoot = draftsContainerRef.current;
      if (currentRoot && draftsHandlerRef.current) {
        currentRoot.removeEventListener('change', draftsHandlerRef.current);
        draftsHandlerRef.current = null;
      }
      return;
    }

    const root = draftsContainerRef.current;
    if (!root || !rulesSections?.drafts) return;

    // Use setTimeout to ensure DOM is ready after dangerouslySetInnerHTML
    const timeoutId = setTimeout(() => {
      const radios = Array.from(
        root.querySelectorAll<HTMLInputElement>('input[type="radio"][name="draft_type"]')
      );

      const sections = Array.from(
        root.querySelectorAll<HTMLElement>('section[data-draft-type]')
      );

      if (!radios.length || !sections.length) {
        return;
      }

      const applySelection = (value: string) => {
        sections.forEach((section) => {
          const matches = section.dataset.draftType === value;
          // Toggle the existing .hidden class
          section.classList.toggle('hidden', !matches);
        });
      };

      const handleChange = (event: Event) => {
        const target = event.target as HTMLInputElement | null;
        if (!target || target.name !== 'draft_type') return;
        applySelection(target.value);
      };

      // Clean up any previous handler
      if (draftsHandlerRef.current) {
        root.removeEventListener('change', draftsHandlerRef.current);
      }

      // Set initial state based on the checked radio (or default to the first one)
      const initiallyChecked = radios.find((r) => r.checked) ?? radios[0];
      if (initiallyChecked) {
        applySelection(initiallyChecked.value);
      }

      // Attach a single change listener on the root to capture radio changes (event delegation)
      root.addEventListener('change', handleChange);
      draftsHandlerRef.current = handleChange;
    }, 0);

    // Cleanup when Drafts is unmounted or tab changes
    return () => {
      clearTimeout(timeoutId);
      const currentRoot = draftsContainerRef.current;
      if (currentRoot && draftsHandlerRef.current) {
        currentRoot.removeEventListener('change', draftsHandlerRef.current);
        draftsHandlerRef.current = null;
      }
    };
  }, [rulesSections?.drafts, currentAboutPage, activeRulesTab]);

  // If no league selected, show message
  if (!leagueId) {
    const pageTitle = currentAboutPage === 'how-it-works' ? 'About: How It Works' :
                     currentAboutPage === 'entry-fees' ? 'About: Entry Fees' :
                     currentAboutPage === 'prize-pool' ? 'About: Prize Pool' :
                     currentAboutPage === 'key-league-dates' ? 'About: Key League Dates' :
                     currentAboutPage === 'rules' ? 'About: Rules' : 'About';
    
    return (
      <Box className="minimal-panel-padding">
        <PageTitle mb={4}>{pageTitle}</PageTitle>
        <Text color="gray.400">Please select a league to view this page.</Text>
      </Box>
    );
  }

  // Reset inner Rules tab when Rules page becomes active
  useEffect(() => {
    if (currentAboutPage === 'rules') {
      setActiveRulesTab('rules-of-the-game');
    }
  }, [currentAboutPage]);

  // Loading state
  if (isLoading) {
    const pageTitle = currentAboutPage === 'how-it-works' ? 'About: How It Works' :
                     currentAboutPage === 'entry-fees' ? 'About: Entry Fees' :
                    currentAboutPage === 'prize-pool' ? 'About: Prize Pool' :
                     currentAboutPage === 'key-league-dates' ? 'About: Key League Dates' :
                     currentAboutPage === 'rules' ? 'About: Rules' : 'About';
    
    return (
      <Box className="minimal-panel-padding">
        <PageTitle mb={4}>{pageTitle}</PageTitle>
        <Center py={10}>
          <Spinner size="xl" />
        </Center>
      </Box>
    );
  }

  // Error state
  if (error) {
    const pageTitle = currentAboutPage === 'how-it-works' ? 'About: How It Works' :
                     currentAboutPage === 'entry-fees' ? 'About: Entry Fees' :
                     currentAboutPage === 'prize-pool' ? 'About: Prize Pool' :
                     currentAboutPage === 'key-league-dates' ? 'About: Key League Dates' :
                     currentAboutPage === 'rules' ? 'About: Rules' : 'About';
    
    return (
      <Box className="minimal-panel-padding">
        <PageTitle mb={4}>{pageTitle}</PageTitle>
        <Text color="red.400">There was an error loading About information.</Text>
      </Box>
    );
  }

  // Render How It Works page
  if (currentAboutPage === 'how-it-works') {
    return (
      <Container maxW="container.xl" className="minimal-panel-padding">
        <VStack spacing={2} align="stretch">
          <PageTitle mb={2}>About: How It Works</PageTitle>
          {howItWorksHtml ? (
            <Box ref={howItWorksRef}>
              <div
                className="about_content"
                dangerouslySetInnerHTML={{ __html: howItWorksHtml }}
              />
            </Box>
          ) : (
            <Text color="gray.400">How It Works content not available.</Text>
          )}
        </VStack>
      </Container>
    );
  }

  // Render Entry Fees page
  if (currentAboutPage === 'entry-fees') {
    return (
      <Container maxW="container.xl" className="minimal-panel-padding">
        <VStack spacing={2} align="stretch">
          <PageTitle mb={2}>About: Entry Fees</PageTitle>
          {entryFeesHtml ? (
            <Box ref={entryFeesRef}>
              <div
                className="about_content"
                dangerouslySetInnerHTML={{ __html: entryFeesHtml }}
              />
            </Box>
          ) : (
            <Text color="gray.400">Entry fees content not available.</Text>
          )}
        </VStack>
      </Container>
    );
  }

  // Render Prize Pool page
  if (currentAboutPage === 'prize-pool') {
    return (
      <Container maxW="container.xl" className="minimal-panel-padding">
        <VStack spacing={2} align="stretch">
          <PageTitle mb={2}>About: Prize Pool</PageTitle>
          {prizePoolHtml ? (
            <Box ref={prizePoolRef}>
              <div
                className="about_content"
                dangerouslySetInnerHTML={{ __html: prizePoolHtml }}
              />
            </Box>
          ) : (
            <Text color="gray.400">Prize pool content not available.</Text>
          )}
        </VStack>
      </Container>
    );
  }

  // Render Key League Dates page
  if (currentAboutPage === 'key-league-dates') {
    return (
      <Container maxW="container.xl" className="minimal-panel-padding">
        <VStack spacing={2} align="stretch">
          <PageTitle mb={2}>About: Key League Dates</PageTitle>
          {keyLeagueDatesHtml ? (
            <Box ref={keyLeagueDatesRef}>
              <div
                className="about_content"
                dangerouslySetInnerHTML={{ __html: keyLeagueDatesHtml }}
              />
            </Box>
          ) : (
            <Text color="gray.400">Key League Dates content not available.</Text>
          )}
        </VStack>
      </Container>
    );
  }

  // Render Rules page with inner tabs
  if (currentAboutPage === 'rules') {
    const rulesTabKeys: RulesTabKey[] = ['rules-of-the-game', 'roster-management', 'compliance', 'free-agency', 'trades', 'drafts'];
    const activeTabIndex = rulesTabKeys.indexOf(activeRulesTab);
    
    return (
      <Container maxW="container.xl" className="minimal-panel-padding">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={3}>
            <PageTitle>About: Rules</PageTitle>
            <Button 
              colorScheme="blue" 
              size="sm" 
              display={{ base: 'inline-flex', md: 'none' }} 
              onClick={onRulesQuestionOpen}
              aria-label="Open rules questions modal"
            >
              QUESTIONS?
            </Button>
          </Flex>

          {rulesSections ? (
            <>
              {/* Mobile: Select dropdown and Desktop: Tabs */}
              <Box mb={4}>
                <Select
                  value={activeRulesTab}
                  onChange={(e) => setActiveRulesTab(e.target.value as RulesTabKey)}
                  display={{ base: 'block', md: 'none' }}
                  size="md"
                  width="200px"
                  mb={2}
                  aria-label="Select rules section"
                >
                  {rulesTabOptions.map(tab => (
                    <option key={tab.value} value={tab.value}>
                      {tab.label}
                    </option>
                  ))}
                </Select>
                <Tabs 
              index={activeTabIndex >= 0 ? activeTabIndex : 0}
              onChange={(index) => {
                setActiveRulesTab(rulesTabKeys[index] || 'rules-of-the-game');
              }}
              colorScheme="blue" 
              variant="enclosed"
            >
              <TabList display={{ base: 'none', md: 'flex' }} alignItems="center">
                <Tab>Rules of the Game</Tab>
                <Tab>Roster Management</Tab>
                <Tab>Compliance</Tab>
                <Tab>Free Agency</Tab>
                <Tab>Trades</Tab>
                <Tab>Drafts</Tab>
                <Box ml="auto" display="flex">
                  <Button colorScheme="blue" size="sm" onClick={onRulesQuestionOpen}>
                    QUESTIONS?
                  </Button>
                </Box>
              </TabList>

            <TabPanels>
              {/* Rules of the Game */}
              <TabPanel>
                {rulesSections.rules_of_the_game ? (
                  <Box>
                    <div
                      className="about_content"
                      dangerouslySetInnerHTML={{ __html: rulesSections.rules_of_the_game }}
                    />
                  </Box>
                ) : (
                  <Text color="gray.400">Rules of the Game content not available.</Text>
                )}
              </TabPanel>

              {/* Roster Management */}
              <TabPanel>
                {rulesSections.roster_management ? (
                  <Box>
                    <div
                      className="about_content"
                      dangerouslySetInnerHTML={{ __html: rulesSections.roster_management }}
                    />
                  </Box>
                ) : (
                  <Text color="gray.400">Roster Management content not available.</Text>
                )}
              </TabPanel>

              {/* Compliance */}
              <TabPanel>
                {rulesSections.compliance ? (
                  <Box>
                    <div
                      className="about_content"
                      dangerouslySetInnerHTML={{ __html: rulesSections.compliance }}
                    />
                  </Box>
                ) : (
                  <Text color="gray.400">Compliance content not available.</Text>
                )}
              </TabPanel>

              {/* Free Agency */}
              <TabPanel>
                {rulesSections.free_agency ? (
                  <Box>
                    <div
                      className="about_content"
                      dangerouslySetInnerHTML={{ __html: rulesSections.free_agency }}
                    />
                  </Box>
                ) : (
                  <Text color="gray.400">Free Agency content not available.</Text>
                )}
              </TabPanel>

              {/* Trades */}
              <TabPanel>
                {rulesSections.trades ? (
                  <Box>
                    <div
                      className="about_content"
                      dangerouslySetInnerHTML={{ __html: rulesSections.trades }}
                    />
                  </Box>
                ) : (
                  <Text color="gray.400">Trades content not available.</Text>
                )}
              </TabPanel>

              {/* Drafts */}
              <TabPanel>
                {rulesSections.drafts ? (
                  <Box>
                    <div
                      ref={draftsContainerRef}
                      className="about_content"
                      dangerouslySetInnerHTML={{ __html: rulesSections.drafts }}
                    />
                  </Box>
                ) : (
                  <Text color="gray.400">Drafts content not available.</Text>
                )}
              </TabPanel>
            </TabPanels>
                </Tabs>
              </Box>
          </>
        ) : (
          <Text color="gray.400">Rules content not available.</Text>
        )}

          {/* Rules Question Modal */}
          <RulesQuestionModal
            isOpen={isRulesQuestionOpen}
            onClose={onRulesQuestionClose}
            question={rulesQuestion}
            setQuestion={setRulesQuestion}
          />
        </VStack>
      </Container>
    );
  }

  // Fallback (should not reach here, but just in case)
  return (
    <Box className="minimal-panel-padding">
      <PageTitle mb={4}>About</PageTitle>
      <Text color="gray.400">Page not found.</Text>
    </Box>
  );
};

export default HowItWorks;
